<script setup lang="ts">
import {
  computed,
  PropType,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from "vue";
import { useElementSize, useTimeoutFn } from "@vueuse/core";
import FeedbackSurveyGreeting from "@/components/Core/PopupMessages/Feedback/FeedbackSurveyGreeting.vue";
import FeedbackSurveyProgress from "@/components/Core/PopupMessages/Feedback/FeedbackSurveyProgress.vue";
import FeedbackSurveyQuestion from "@/components/Core/PopupMessages/Feedback/FeedbackSurveyQuestion.vue";
import FeedbackThanks from "@/components/Core/PopupMessages/Feedback/FeedbackThanks.vue";
import {
  FEEDBACK_ADVANCE_DELAY_MS,
  FEEDBACK_CLOSE_DELAY_MS,
  FeedbackAnswer,
  FeedbackQuestion,
  FeedbackSendState,
  FeedbackSurveyDefinition,
  FeedbackSurveyDraft,
  FeedbackSurveyResult,
} from "@/components/Core/PopupMessages/Feedback/feedbackSurvey";
import { FEEDBACK_COPY } from "@/copy/feedback";

const props = defineProps({
  survey: {
    type: Object as PropType<FeedbackSurveyDefinition>,
    required: true,
  },
  // Undefined where nothing is sent (sandbox, preview): thank right away.
  sendState: {
    type: String as PropType<FeedbackSendState>,
    default: undefined,
  },
  draft: {
    type: Object as PropType<FeedbackSurveyDraft>,
    default: undefined,
  },
});
const emit = defineEmits({
  accept: () => true,
  submit: (_result: FeedbackSurveyResult) => true,
  progress: (_result: FeedbackSurveyResult) => true,
  decline: () => true,
  retry: () => true,
  dismiss: () => true,
  // The question index it was closed on; undefined from the thanks screen.
  close: (_step: number | undefined) => true,
});

const questions = computed(() => props.survey.questions);
const followUpQuestionId = computed(
  () =>
    questions.value.find(
      (candidate) => candidate.type === "text" && candidate.followUp,
    )?.id,
);
const stage = shallowRef<"intro" | "questions" | "thanks">("intro");
const step = shallowRef(0);
const answers = ref<Record<string, FeedbackAnswer>>(restoredAnswers());
// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- the draft only seeds the survey
const followUp = shallowRef(props.draft?.followUp ?? false);
const sentFollowUp = shallowRef(false);
let closed = false;
const question = computed(() => questions.value[step.value]);

function restoredAnswers(): Record<string, FeedbackAnswer> {
  const restored: Record<string, FeedbackAnswer> = {};
  for (const { question: id, value } of props.draft?.answers ?? []) {
    const question = questions.value.find((candidate) => candidate.id === id);
    if (question && fitsQuestion(question, value)) restored[id] = value;
  }
  const commentId = followUpQuestionId.value;
  if (commentId !== undefined && props.draft?.comment) {
    restored[commentId] = props.draft.comment;
  }
  return restored;
}
function fitsQuestion(question: FeedbackQuestion, value: FeedbackAnswer) {
  if (question.type === "scale") {
    return (
      typeof value === "number" &&
      value >= question.min &&
      value <= question.max
    );
  }
  if (question.type === "choice") {
    return question.options.some((option) => option.value === value);
  }
  return typeof value === "string";
}

const isLastStep = computed(() => step.value === questions.value.length - 1);
const view = computed(() =>
  props.sendState === "failed" ? "thanks" : stage.value,
);
// Outgoing content is lifted out of the flow so the card measures only the
// incoming content and glides there in one move. Content mid-swap ignores the
// pointer, so a double click can't answer the question that fades in.
const SWAP = {
  enterActiveClass:
    "pointer-events-none transition duration-150 delay-50 ease-out motion-reduce:transition-none",
  enterFromClass: "translate-y-1 opacity-0",
  leaveActiveClass:
    "pointer-events-none absolute inset-x-0 top-0 transition duration-75 ease-in motion-reduce:transition-none",
  leaveToClass: "opacity-0",
};

// Height `auto` can't transition, so the card follows its content in pixels.
const content = useTemplateRef("content");
const { height: contentHeight } = useElementSize(content, undefined, {
  box: "border-box",
});
const cardHeight = computed(() =>
  contentHeight.value > 0 ? `${contentHeight.value}px` : undefined,
);

// The delay lets users see their pick before the question changes.
const advance = useTimeoutFn(
  () => {
    step.value += 1;
  },
  FEEDBACK_ADVANCE_DELAY_MS,
  { immediate: false },
);
const closeAfterThanks = useTimeoutFn(close, FEEDBACK_CLOSE_DELAY_MS, {
  immediate: false,
});
watch(followUp, () => {
  if (stage.value === "questions") emitProgress();
});
// Waits for the save, so a failed send can still be shown.
watch([stage, () => props.sendState], ([currentStage, sendState]) => {
  if (currentStage !== "thanks") return;
  if (sendState === "pending" || sendState === "failed") {
    closeAfterThanks.stop();
  } else {
    closeAfterThanks.start();
  }
});

function accept() {
  const firstUnanswered = questions.value.findIndex(
    ({ id }) => answers.value[id] === undefined,
  );
  step.value =
    firstUnanswered === -1 ? questions.value.length - 1 : firstUnanswered;
  stage.value = "questions";
  emit("accept");
}

function answer(value: FeedbackAnswer) {
  if (!question.value || advance.isPending.value) return;
  answers.value[question.value.id] = value;
  if (!isLastStep.value) emitProgress();
  next();
}

// So Back, Close and a reload don't lose a half-typed comment.
function saveDraft(value: string) {
  if (!question.value) return;
  if (value.trim()) answers.value[question.value.id] = value;
  else delete answers.value[question.value.id];
  emitProgress();
}

function skip() {
  if (question.value) delete answers.value[question.value.id];
  followUp.value = false;
  emitProgress();
  next();
}

function next() {
  if (isLastStep.value) finish();
  else advance.start();
}

function back() {
  advance.stop();
  step.value -= 1;
}

function emitProgress() {
  emit("progress", result(false));
}

function result(completed: boolean): FeedbackSurveyResult {
  const commentId = followUpQuestionId.value;
  const comment =
    commentId === undefined ? undefined : answers.value[commentId];
  const text = typeof comment === "string" ? comment.trim() : "";
  return {
    answers: Object.fromEntries(
      Object.entries(answers.value).filter(([id]) => id !== commentId),
    ),
    comment: text,
    followUp: followUp.value && text !== "",
    completed,
  };
}

// The guards stop a double Enter or a second click during the exit animation
// from submitting twice.
function finish() {
  if (closed || stage.value !== "questions") return;
  const submission = result(true);
  sentFollowUp.value = submission.followUp;
  emit("submit", submission);
  stage.value = "thanks";
}

// Leaving halfway still counts as taking part, so keep what they answered.
function close() {
  if (closed) return;
  closed = true;
  if (stage.value === "questions" && Object.keys(answers.value).length > 0) {
    emit("submit", result(false));
  }
  emit("close", stage.value === "questions" ? step.value : undefined);
}

function closeThanks() {
  if (props.sendState === "failed") emit("dismiss");
  else close();
}

function onEscape() {
  if (view.value === "intro") emit("decline");
  else if (view.value === "thanks") closeThanks();
  else close();
}
</script>

<template>
  <!-- The parent's enter/leave classes land here, clear of the card's height transition. -->
  <div class="w-full origin-bottom-right">
    <section
      :aria-label="survey.label"
      class="animate-attention-ping pointer-events-auto w-full overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-gray-200 transition-[height] duration-250 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none"
      :style="{ height: cardHeight }"
      @keydown.esc="onEscape"
    >
      <div ref="content">
        <div class="relative">
          <Transition v-bind="SWAP">
            <FeedbackSurveyGreeting
              v-if="view === 'intro'"
              :invite="survey.invite"
            />
            <FeedbackSurveyProgress
              v-else-if="view === 'questions'"
              :step="step"
              :total="questions.length"
              @back="back"
              @close="close"
            />
            <FeedbackThanks
              v-else
              :follow-up="sentFollowUp && sendState !== 'pending'"
              :failed="sendState === 'failed'"
              @retry="emit('retry')"
              @close="closeThanks"
            />
          </Transition>
        </div>
        <div class="relative">
          <Transition v-bind="SWAP">
            <div
              v-if="view === 'intro'"
              key="intro"
              class="flex items-center justify-between pr-4 pb-4 pl-6"
            >
              <button
                type="button"
                class="btn-basic btn-sm -ml-2.5"
                @click="emit('decline')"
              >
                {{ FEEDBACK_COPY.decline }}
              </button>
              <button type="button" class="btn-primary btn-sm" @click="accept">
                {{ FEEDBACK_COPY.accept }}
              </button>
            </div>
            <FeedbackSurveyQuestion
              v-else-if="view === 'questions' && question"
              :key="question.id"
              v-model:follow-up="followUp"
              class="px-4 pb-4"
              :question="question"
              :answer="answers[question.id]"
              :last="isLastStep"
              @answer="answer"
              @draft="saveDraft"
              @skip="skip"
            />
          </Transition>
        </div>
      </div>
    </section>
  </div>
</template>
